"use client";

import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  type PromptInputMessage,
  PromptInputSpeechButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
  PromptInputHeader,
  usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input";
import { Assistant, PublicAssistant } from "@/generated/types.gen";
import { ChatStatus } from "ai";
import {
  GlobeIcon,
  SquareIcon,
  EditIcon,
  ExternalLink,
  ListTodo,
  TriangleAlert as TriangleAlertIcon,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { FormEvent, useEffect, useMemo, useRef } from "react";
import { useLocale, useTranslations } from "next-intl";
import { DICTATION_LANGUAGE, resolveLocale } from "@/i18n/locales";
import { customWidgetCopy } from "@/i18n/widgetCopy";
import { Button } from "@/components/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { TodoPanel } from "./TodoPanel";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";

interface INoxusPromptInput {
  handleSubmit: (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>
  ) => void | Promise<void>;
  setText: (text: string) => any;
  text: string;
  // Bumped when the text is filled from outside (an Ask Genie request), so the
  // user can send it with Enter.
  focusRequest?: number;
  status: ChatStatus;
  assistantData: Assistant | PublicAssistant | undefined;
  groupId: string;
  conversationId: string;
  configChanged?: boolean;
  onStartNewChat?: () => void;
  isPublic?: boolean;
  todos?: Array<{ id?: string; content: string; status: string }>;
  todoPanelOpen?: boolean;
  onTodoPanelOpenChange?: (open: boolean) => void;
  onStop?: () => void;
  // Disables the Stop button once an interrupt is in flight, so rapid
  // repeated clicks can't re-fire the teardown.
  stopDisabled?: boolean;
  // Sticky guardrail lock — when set, the input is disabled and this
  // message is shown above the input as a destructive notice. Driven by
  // PIIConfig.block_after_violations on the backend; null means unlocked.
  lockedMessage?: string | null;
  // Non-destructive disable — same lock semantics (input disabled,
  // banner above), but yellow/warning style. Used when the assistant
  // can't dispatch a message because the workspace has no valid models
  // configured. Distinct from ``lockedMessage`` because that's a
  // permanent safety hold; this resolves the moment the user wires up
  // a working model.
  warningMessage?: string | null;
  // Only the public deployment renderer passes this. Everywhere inside the
  // platform leaves it unset, so the Noxus attribution stays hidden.
  showPoweredBy?: boolean;
  isGenie?: boolean;
}

const ConditionalHeader = ({ isPublic }: { isPublic?: boolean }) => {
  const { files } = usePromptInputAttachments();

  if (files.length === 0) return null;

  return (
    <PromptInputHeader className="pb-0">
      <PromptInputAttachments>
        {(attachment) => (
          <PromptInputAttachment data={attachment} isPublic={isPublic} />
        )}
      </PromptInputAttachments>
    </PromptInputHeader>
  );
};

export const PromptInputSubmitWithReadyCheck = ({
  text,
  status,
  disabled = false,
}: {
  text: string;
  status: ChatStatus;
  disabled?: boolean;
}) => {
  const { files } = usePromptInputAttachments();
  // "error" sends too: a turn that died mid-stream would otherwise leave the
  // composer permanently disabled, recoverable only by reloading (DEV-1737).
  const canSend = status === "ready" || status === "error";
  const isReady =
    canSend && ((!!text && text.trim() !== "") || files.length > 0);

  // Present a sendable composer as ready: PromptInputSubmit renders the X
  // (cancel/failure) glyph for "error", which misreads on a button that now
  // retries the message.
  return (
    <PromptInputSubmit
      disabled={disabled || !isReady}
      status={canSend ? "ready" : status}
    />
  );
};

const StopButton = ({
  onStop,
  disabled = false,
}: {
  onStop?: () => void;
  disabled?: boolean;
}) => {
  const t = useTranslations("widget.composer");
  return (
    <PromptInputButton
      onClick={onStop}
      disabled={disabled}
      aria-label={t("stop")}
      variant="default"
      className="size-8"
    >
      <SquareIcon className="size-3 fill-current" />
    </PromptInputButton>
  );
};

export const NoxusPromptInput = ({
  handleSubmit,
  setText,
  text,
  status,
  assistantData,
  groupId,
  conversationId,
  configChanged,
  onStartNewChat,
  isPublic,
  todos = [],
  todoPanelOpen = false,
  onTodoPanelOpenChange,
  onStop,
  stopDisabled,
  lockedMessage,
  warningMessage,
  showPoweredBy: showPoweredByOverride,
  isGenie,
  focusRequest,
}: INoxusPromptInput) => {
  const t = useTranslations("widget");
  const locale = resolveLocale(useLocale());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!focusRequest || !textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [focusRequest]);
  const def =
    assistantData && "definition" in assistantData
      ? assistantData.definition
      : undefined;

  const showsAttachFile = useMemo(() => {
    if (!assistantData) return false;
    if ("definition" in assistantData) {
      return (assistantData?.definition?.tools as any[] | undefined)?.some(
        (t) => t.type === "files" && t.enabled
      );
    }
    if ("file_upload_enabled" in assistantData) {
      return assistantData.file_upload_enabled;
    }
    return false;
  }, [assistantData]);

  const showsTodoButton = useMemo(() => {
    if (!assistantData || !("definition" in assistantData)) return false;
    return (
      (assistantData?.definition?.tools as any[] | undefined)?.some(
        (t) => t.type === "todos" && t.enabled
      ) ?? false
    );
  }, [assistantData]);

  // Word/Excel editing is offered on-demand when such a file is in the
  // session, so allow uploading those types whenever the Files tool is on.
  const hasFilesCapability = useMemo(() => {
    if (!assistantData || !("definition" in assistantData)) return false;
    return (
      (assistantData?.definition?.tools as any[] | undefined)?.some(
        (t) => t.type === "files" && t.enabled
      ) ?? false
    );
  }, [assistantData]);

  const hasExcelCapability = hasFilesCapability;
  const hasDocxCapability = hasFilesCapability;

  const acceptTypes = useMemo(() => {
    const parts = [
      "application/pdf",
      "image/*",
      // Any text file works: unsupported-by-the-model types are inlined as
      // text server-side (convert_unsupported_files_to_text).
      "text/*",
      "application/json",
      "application/xml",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-powerpoint",
    ];
    if (hasExcelCapability) {
      parts.push(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel"
      );
    }
    if (hasDocxCapability) {
      parts.push(
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
    }
    return parts.join(", ");
  }, [hasExcelCapability, hasDocxCapability]);

  const chatInputPlaceholder =
    (def?.chat_input_placeholder as string | undefined) ??
    (assistantData && "chat_input_placeholder" in assistantData
      ? (assistantData.chat_input_placeholder as string | undefined)
      : undefined);

  const showPoweredBy = showPoweredByOverride ?? false;

  const isAgentDeleted =
    !!assistantData &&
    "deleted_at" in assistantData &&
    assistantData.deleted_at != null;
  const isLocked = !!lockedMessage;
  const isWarning = !!warningMessage && !isLocked;
  const isDisabled = isLocked || isWarning || isAgentDeleted;
  // Placeholder stays the configured chat-input copy even when locked —
  // the red banner above the input already carries the lock message, so
  // double-printing it inside the textarea would be noisy.
  const placeholder =
    customWidgetCopy(chatInputPlaceholder, "chatInputPlaceholder") ??
    t("composer.placeholder");

  return (
    <div
      className={cn(
        "flex flex-col",
        !showPoweredBy && "pb-6",
        isGenie && "pb-[12px]"
      )}
    >
      {configChanged && !isLocked && !isWarning && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-t-md border border-b-0 border-input bg-gray-50 text-muted-foreground text-sm">
          <span>Configuration changes won&apos;t reflect in this chat.</span>
          <Button type="button" onClick={onStartNewChat} variant="outline">
            <EditIcon />
            Start new chat
          </Button>
        </div>
      )}
      {isLocked && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 px-3 py-2 rounded-t-md border border-b-0 border-destructive/40 bg-destructive/10 text-destructive text-sm"
        >
          <span>{lockedMessage}</span>
          {onStartNewChat && (
            <Button
              type="button"
              onClick={onStartNewChat}
              variant="ghostDestructive"
            >
              <EditIcon />
              Start new chat
            </Button>
          )}
        </div>
      )}
      {isWarning && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 px-3 py-2 rounded-t-md border border-b-0 border-warning-500/50 bg-warning-50 text-warning-700 text-sm"
        >
          <div className="flex items-center gap-2 min-w-0">
            <TriangleAlertIcon className="size-4 shrink-0" />
            <span>{warningMessage}</span>
          </div>
          <Button
            type="button"
            variant="ghostWarning"
            size="xs"
            className="shrink-0"
            onClick={() =>
              window.open("/settings/platform/models", "_blank", "noopener")
            }
          >
            <ExternalLink size={12} />
            Go to Models
          </Button>
        </div>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <PromptInput
            onSubmit={handleSubmit}
            accept={acceptTypes}
            globalDrop
            disableDrop={!showsAttachFile || isDisabled}
            rejectionMessage={
              showsAttachFile
                ? t("composer.cannotSendNow")
                : t("composer.cannotReceiveFiles")
            }
            multiple
            className={cn(
              (configChanged || isDisabled) && "[&>div]:rounded-t-none",
              isDisabled && "opacity-60"
            )}
          >
            <ConditionalHeader isPublic={isPublic} />
            <PromptInputBody>
              <PromptInputTextarea
                onChange={(e) => setText(e.target.value)}
                ref={textareaRef}
                value={text}
                placeholder={placeholder}
                disabled={isDisabled}
              />
            </PromptInputBody>
            <PromptInputFooter>
              <PromptInputTools>
                {showsAttachFile && (
                  <PromptInputActionMenu>
                    <PromptInputActionMenuTrigger />
                    <PromptInputActionMenuContent>
                      <PromptInputActionAddAttachments
                        label={t("composer.addFiles")}
                      />
                    </PromptInputActionMenuContent>
                  </PromptInputActionMenu>
                )}
                <PromptInputSpeechButton
                  onTranscriptionChange={setText}
                  textareaRef={textareaRef}
                  defaultLanguage={DICTATION_LANGUAGE[locale]}
                />
                {showsTodoButton && (
                  <Popover
                    open={todoPanelOpen}
                    onOpenChange={onTodoPanelOpenChange}
                  >
                    <PopoverTrigger asChild>
                      <PromptInputButton
                        aria-label={t("composer.viewTasks")}
                        className="relative"
                      >
                        <ListTodo className="size-4" />
                        {(() => {
                          const pendingCount = todos.filter(
                            (t) => t.status !== "completed"
                          ).length;
                          return pendingCount > 0 ? (
                            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-medium text-primary-foreground">
                              {pendingCount}
                            </span>
                          ) : null;
                        })()}
                      </PromptInputButton>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      align="start"
                      className="p-0 w-auto"
                      sideOffset={8}
                    >
                      <TodoPanel
                        todos={todos}
                        onClose={() => onTodoPanelOpenChange?.(false)}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </PromptInputTools>
              {status === "streaming" || status === "submitted" ? (
                <StopButton onStop={onStop} disabled={stopDisabled} />
              ) : (
                <PromptInputSubmitWithReadyCheck
                  text={text}
                  status={status}
                  disabled={isDisabled}
                />
              )}
            </PromptInputFooter>
          </PromptInput>
        </TooltipTrigger>
        {isAgentDeleted && (
          <TooltipContent className="z-10000">
            <p>
              You cannot continue this conversation because the agent is
              deleted. Restore the agent first to continue chatting.
            </p>
          </TooltipContent>
        )}
      </Tooltip>
      {showPoweredBy && (
        <div className="flex items-center justify-center text-center pb-[10px]">
          <Link href="https://noxus.ai" target="_blank">
            <div className="flex items-center justify-center text-center gap-1 py-3">
              <p className="text-[10px] text-muted-foreground">
                {t("history.poweredBy")}
              </p>
              <Image
                src="https://storage.googleapis.com/image-storage-spot-manual/misc/noxus-gray-foreground.svg"
                alt="Noxus Logo"
                width={38}
                height={8}
              />
            </div>
          </Link>
        </div>
      )}
    </div>
  );
};
